import { useState, useCallback, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Divider,
  Chip,
  Button,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AccountBalanceWallet as CreditIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import type { FirmResponse, UpdateFirmRequest } from "@taxassist/api-client";
import {
  useUpdateFirmOps,
  useGetOpsTaxSoftware,
  useGetOpsOfficeLocations,
} from "@taxassist/api-client";
import { UsersTab } from "./UsersTab";
import { CreditBatchesTable } from "../common/CreditBatchesTable";
import { AddCreditBatchDialog } from "./AddCreditBatchDialog";
import { CreditHistory } from "./CreditHistory";
import { NotificationSnackbar } from "../common/NotificationSnackbar";
import { useAuth } from "../../contexts/AuthContext";
import {
  canAddCredits,
  canViewCreditBatches,
  canViewCreditHistory,
} from "../../utils/creditPermissions";
import { useQueryClient } from "@tanstack/react-query";

interface FirmDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  firm: FirmResponse | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`firm-tabpanel-${index}`}
      aria-labelledby={`firm-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export function FirmDetailsDrawer({
  open,
  onClose,
  firm,
}: FirmDetailsDrawerProps) {
  const [currentTab, setCurrentTab] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAddCreditDialogOpen, setIsAddCreditDialogOpen] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Handle drawer close with proper focus management to prevent aria-hidden warnings
  const handleDrawerClose = useCallback(() => {
    // Clear any active focus before closing to prevent aria-hidden violations
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement.blur
    ) {
      document.activeElement.blur();
    }
    onClose();
  }, [onClose]);

  // Get fresh firm data from cache when drawer is open
  const cachedFirmData = queryClient.getQueryData<FirmResponse>([
    `/firms/${firm?.firmId}`,
  ]);

  // Use cached data if available (reflects mutations), fallback to prop
  const currentFirm = cachedFirmData || firm;

  // Check if user is admin (only admins can edit firm details)
  const isAdmin = user?.role === "admin" || user?.groups?.includes("admin");

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setCurrentTab(newValue);
    },
    [],
  );

  const handleSnackbarClose = useCallback(() => {
    setSuccessMessage(null);
    setErrorMessage(null);
  }, []);

  const showSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
  }, []);

  const showError = useCallback((message: string) => {
    setErrorMessage(message);
  }, []);

  const handleOpenAddCreditDialog = useCallback(() => {
    setIsAddCreditDialogOpen(true);
  }, []);

  const handleCloseAddCreditDialog = useCallback(() => {
    setIsAddCreditDialogOpen(false);
  }, []);

  const handleCreditBatchSuccess = useCallback(
    (message: string) => {
      showSuccess(message);
      // Refresh firm data to show updated credits
      if (firm) {
        queryClient.invalidateQueries({ queryKey: [`/firms/${firm.firmId}`] });
        // Also refresh credit history and audit trail data
        queryClient.invalidateQueries({
          queryKey: ["audit-trail", firm.firmId],
        });
      }
    },
    [firm, queryClient, showSuccess],
  );

  const handleCreditBatchError = useCallback(
    (message: string) => {
      showError(message);
    },
    [showError],
  );

  if (!firm) {
    return null;
  }

  // Don't render if no firm data
  if (!currentFirm) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 600, md: 700, lg: 800 },
            maxWidth: "90vw",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Drawer>
    );
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 600, md: 700, lg: 800 },
            maxWidth: "90vw",
          },
        }}
        ModalProps={{
          // Prevent focus issues during drawer transitions
          keepMounted: false,
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BusinessIcon color="primary" />
              <Box>
                <Typography variant="h6" component="h2">
                  {currentFirm?.name}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <Chip
                    label={currentFirm?.isActive ? "Active" : "Inactive"}
                    color={currentFirm?.isActive ? "success" : "default"}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    ID: {currentFirm?.firmId}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <IconButton
              onClick={handleDrawerClose}
              edge="end"
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="firm details tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                icon={<BusinessIcon />}
                label="Details"
                id="firm-tab-0"
                aria-controls="firm-tabpanel-0"
                iconPosition="start"
              />
              <Tab
                icon={<CreditIcon />}
                label="Credits"
                id="firm-tab-1"
                aria-controls="firm-tabpanel-1"
                iconPosition="start"
              />
              <Tab
                icon={<PeopleIcon />}
                label="Users"
                id="firm-tab-2"
                aria-controls="firm-tabpanel-2"
                iconPosition="start"
              />
              <Tab
                icon={<SettingsIcon />}
                label="Settings"
                id="firm-tab-3"
                aria-controls="firm-tabpanel-3"
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <TabPanel value={currentTab} index={0}>
              <FirmDetailsTab
                firm={currentFirm}
                isAdmin={!!isAdmin}
                onSuccess={showSuccess}
                onError={showError}
              />
            </TabPanel>
            <TabPanel value={currentTab} index={1}>
              <CreditManagementTab
                firm={currentFirm}
                user={user}
                onOpenAddCreditDialog={handleOpenAddCreditDialog}
              />
            </TabPanel>
            <TabPanel value={currentTab} index={2}>
              <UsersTab
                firm={currentFirm}
                onSuccess={showSuccess}
                onError={showError}
              />
            </TabPanel>
            <TabPanel value={currentTab} index={3}>
              <FirmSettingsTab
                firm={currentFirm}
                isAdmin={!!isAdmin}
                onSuccess={showSuccess}
                onError={showError}
              />
            </TabPanel>
          </Box>
        </Box>
      </Drawer>

      {/* Success/Error Snackbars */}
      <NotificationSnackbar
        message={successMessage}
        onClose={handleSnackbarClose}
        severity="success"
      />

      <NotificationSnackbar
        message={errorMessage}
        onClose={handleSnackbarClose}
        severity="error"
      />

      {/* Add Credit Batch Dialog */}
      {firm && (
        <AddCreditBatchDialog
          open={isAddCreditDialogOpen}
          onClose={handleCloseAddCreditDialog}
          firm={firm}
          onSuccess={handleCreditBatchSuccess}
          onError={handleCreditBatchError}
        />
      )}
    </>
  );
}

// Firm Details Tab Component - matches Jobs view pattern
function FirmDetailsTab({
  firm,
  isAdmin,
  onSuccess,
  onError,
}: {
  firm: FirmResponse;
  isAdmin: boolean;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedFirm, setEditedFirm] = useState(firm);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const queryClient = useQueryClient();

  const updateFirmMutation = useUpdateFirmOps({
    mutation: {
      onSuccess: (updatedFirmData) => {
        setIsEditing(false);
        // Update cache directly with response data instead of invalidating
        queryClient.setQueryData([`/firms/${firm.firmId}`], updatedFirmData);
        onSuccess?.("Firm updated successfully");
      },
      onError: (error: any) => {
        console.error("Failed to save firm:", error);
        onError?.("Failed to update firm. Please try again.");
      },
    },
  });

  const statusToggleMutation = useUpdateFirmOps({
    mutation: {
      onSuccess: (updatedFirmData) => {
        setShowStatusDialog(false);
        // Update cache directly with response data instead of invalidating
        queryClient.setQueryData([`/firms/${firm.firmId}`], updatedFirmData);
        const action = !firm.isActive ? "activated" : "deactivated";
        onSuccess?.(`Firm ${action} successfully`);
      },
      onError: (error: any) => {
        console.error("Failed to update firm status:", error);
        onError?.("Failed to update firm status. Please try again.");
      },
    },
  });

  const handleEdit = () => {
    setEditedFirm(firm);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedFirm(firm);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const updateData: UpdateFirmRequest = {
      name: editedFirm.name,
      email: editedFirm.email || undefined,
      phone: editedFirm.phone || undefined,
    };

    updateFirmMutation.mutate({
      firmId: firm.firmId,
      data: updateData,
    });
  };

  const handleStatusToggle = async () => {
    const updateData: UpdateFirmRequest = {
      isActive: !firm.isActive,
    };

    statusToggleMutation.mutate({
      firmId: firm.firmId,
      data: updateData,
    });
  };

  const handleFieldChange = (field: string, value: string) => {
    setEditedFirm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h6">Basic Information</Typography>
          {!isEditing && isAdmin ? (
            <IconButton onClick={handleEdit} size="small" color="primary">
              <EditIcon />
            </IconButton>
          ) : isEditing ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                onClick={handleSave}
                size="small"
                color="primary"
                disabled={updateFirmMutation.isPending}
              >
                {updateFirmMutation.isPending ? (
                  <CircularProgress size={20} />
                ) : (
                  <SaveIcon />
                )}
              </IconButton>
              <IconButton
                onClick={handleCancel}
                size="small"
                color="secondary"
                disabled={updateFirmMutation.isPending}
              >
                <CancelIcon />
              </IconButton>
            </Box>
          ) : null}
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Basic Info Fields - matching Jobs view pattern */}
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <BusinessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" fontWeight={500}>
                Firm Name
              </Typography>
            </Box>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editedFirm.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                disabled={updateFirmMutation.isPending}
                sx={{ ml: 2.5 }}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 2.5 }}
              >
                {firm.name}
              </Typography>
            )}
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" fontWeight={500}>
                Email
              </Typography>
            </Box>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                type="email"
                value={editedFirm.email || ""}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                disabled={updateFirmMutation.isPending}
                sx={{ ml: 2.5 }}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 2.5 }}
              >
                {firm.email || "N/A"}
              </Typography>
            )}
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" fontWeight={500}>
                Phone
              </Typography>
            </Box>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editedFirm.phone || ""}
                onChange={(e) => handleFieldChange("phone", e.target.value)}
                disabled={updateFirmMutation.isPending}
                sx={{ ml: 2.5 }}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 2.5 }}
              >
                {firm.phone || "N/A"}
              </Typography>
            )}
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AssignmentIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" fontWeight={500}>
                Status
              </Typography>
            </Box>
            <Box sx={{ ml: 2.5 }}>
              <Chip
                label={firm.isActive ? "Active" : "Inactive"}
                color={firm.isActive ? "success" : "default"}
                size="small"
              />
            </Box>
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CalendarTodayIcon
                sx={{ fontSize: 16, color: "text.secondary" }}
              />
              <Typography variant="body2" fontWeight={500}>
                Created
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2.5 }}>
              {new Date(firm.createdAt).toLocaleDateString()}
            </Typography>
            {firm.createdByEmail && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 2.5, display: "block" }}
              >
                by {firm.createdByEmail}
              </Typography>
            )}
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CreditIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" fontWeight={500}>
                Total Credits
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2.5 }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {firm.totalCredits}
              </Typography>
              {firm.expiringCredits > 0 && (
                <Chip
                  label={`${firm.expiringCredits} expiring`}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Status Action Button - Bottom Left (Admin Only) */}
        {isAdmin && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mt: 3,
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Button
              variant="outlined"
              color={firm.isActive ? "warning" : "success"}
              size="small"
              onClick={() => setShowStatusDialog(true)}
              disabled={statusToggleMutation.isPending}
            >
              {firm.isActive ? "Deactivate Firm" : "Activate Firm"}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog
        open={showStatusDialog}
        onClose={() => setShowStatusDialog(false)}
      >
        <DialogTitle>
          Confirm {firm.isActive ? "Deactivation" : "Activation"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {firm.isActive ? "deactivate" : "activate"}{" "}
            "{firm.name}"?
            {firm.isActive &&
              " This will prevent new job submissions for this firm."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowStatusDialog(false)}
            disabled={statusToggleMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStatusToggle}
            color={firm.isActive ? "warning" : "success"}
            variant="contained"
            disabled={statusToggleMutation.isPending}
          >
            {statusToggleMutation.isPending ? (
              <CircularProgress size={20} />
            ) : firm.isActive ? (
              "Deactivate"
            ) : (
              "Activate"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Credit Management Tab Component
function CreditManagementTab({
  firm,
  user,
  onOpenAddCreditDialog,
}: {
  firm: FirmResponse;
  user: any;
  onOpenAddCreditDialog: () => void;
}) {
  const getCreditColor = (credits: number) => {
    if (credits === 0) return "error.main";
    if (credits < 10) return "warning.main";
    return "success.main";
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Credit Summary Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h6">Credit Management</Typography>
          {canAddCredits(user?.role) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CreditIcon />}
              onClick={onOpenAddCreditDialog}
            >
              Add Credits
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="h3" color={getCreditColor(firm.totalCredits)}>
            {firm.totalCredits}
          </Typography>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Available Credits
            </Typography>
            {firm.expiringCredits > 0 && (
              <Typography variant="caption" color="warning.main">
                {firm.expiringCredits} expiring in 30 days
              </Typography>
            )}
          </Box>
        </Box>

        {firm.totalCredits === 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            No credits available. Job submissions will be blocked until credits
            are added.
          </Alert>
        )}

        {firm.expiringCredits > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {firm.expiringCredits} credits will expire within the next 30 days.
            Consider adding new credits before they expire.
          </Alert>
        )}
      </Paper>

      {/* Credit Batches Section */}
      {canViewCreditBatches(user?.role) && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Credit Batches
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {firm.creditBatches && firm.creditBatches.length > 0 ? (
            <CreditBatchesTable batches={firm.creditBatches} />
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              No credit batches found
            </Typography>
          )}
        </Paper>
      )}

      {/* Credit History Section */}
      {canViewCreditHistory(user?.role) && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Credit History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <CreditHistory firm={firm} limit={10} />
        </Paper>
      )}
    </Box>
  );
}

// Firm Settings Tab Component - with SurePrep Configuration
function FirmSettingsTab({
  firm,
  isAdmin,
  onSuccess,
  onError,
}: {
  firm: FirmResponse;
  isAdmin: boolean;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedConfig, setEditedConfig] = useState(firm?.surePrepConfig);
  const queryClient = useQueryClient();

  // Update editedConfig when firm data changes (including cache updates)
  useEffect(() => {
    if (!isEditing && firm?.surePrepConfig) {
      setEditedConfig(firm.surePrepConfig);
    }
  }, [firm, isEditing]);

  // Fetch and cache tax software list globally (once loaded, stays cached)
  const {
    data: taxSoftwareList,
    isLoading: isLoadingTaxSoftware,
    error: taxSoftwareError,
  } = useGetOpsTaxSoftware({
    query: {
      queryKey: ["opsTaxSoftware"],
      enabled: isAdmin, // Load when admin user opens drawer (not just when editing)
      staleTime: Infinity, // Tax software list rarely changes, cache indefinitely
      gcTime: 30 * 60 * 1000, // Keep in memory for 30 minutes after last use
    },
  });

  // Fetch and cache office locations list globally (once loaded, stays cached)
  const {
    data: officeLocationList,
    isLoading: isLoadingOfficeLocations,
    error: officeLocationsError,
  } = useGetOpsOfficeLocations({
    query: {
      queryKey: ["opsOfficeLocations"],
      enabled: isAdmin, // Load when admin user opens drawer (not just when editing)
      staleTime: Infinity, // Office locations rarely change, cache indefinitely
      gcTime: 30 * 60 * 1000, // Keep in memory for 30 minutes after last use
    },
  });

  const updateConfigMutation = useUpdateFirmOps({
    mutation: {
      onSuccess: (updatedFirmData) => {
        setIsEditing(false);

        // Ensure tax software name is populated from cached tax software list
        if (updatedFirmData?.surePrepConfig?.taxSoftwareId && taxSoftwareList) {
          const taxSoftware = taxSoftwareList.find(
            (sw) => sw.Id === updatedFirmData.surePrepConfig.taxSoftwareId,
          );
          if (taxSoftware && !updatedFirmData.surePrepConfig.taxSoftwareName) {
            updatedFirmData.surePrepConfig.taxSoftwareName = taxSoftware.Name;
          }
        }

        // Update cache directly with enhanced response data
        queryClient.setQueryData([`/firms/${firm.firmId}`], updatedFirmData);
        onSuccess?.("SurePrep configuration updated successfully");
      },
      onError: (error: any) => {
        console.error("Failed to save SurePrep config:", error);
        onError?.("Failed to update SurePrep configuration. Please try again.");
      },
    },
  });

  const handleEdit = () => {
    setEditedConfig(firm?.surePrepConfig);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedConfig(firm?.surePrepConfig);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const updateData: UpdateFirmRequest = {
      surePrepConfig: {
        officeLocationId: editedConfig.officeLocationId,
        accountNumber: editedConfig.accountNumber,
        taxSoftwareId: editedConfig.taxSoftwareId,
        taxSoftwareName: editedConfig.taxSoftwareName,
      },
    };

    updateConfigMutation.mutate({
      firmId: firm.firmId,
      data: updateData,
    });
  };

  const handleConfigFieldChange = (field: string, value: string | number) => {
    setEditedConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleTaxSoftwareChange = (taxSoftwareId: number) => {
    const selectedTaxSoftware = taxSoftwareList?.find(
      (software) => software.Id === taxSoftwareId,
    );
    setEditedConfig((prev) => ({
      ...prev,
      taxSoftwareId,
      taxSoftwareName: selectedTaxSoftware?.Name || undefined,
    }));
  };

  const handleOfficeLocationChange = (officeLocationId: number) => {
    setEditedConfig((prev) => ({
      ...prev,
      officeLocationId,
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      {firm?.surePrepConfig && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h6">SurePrep Config</Typography>
            {!isEditing && isAdmin ? (
              <IconButton onClick={handleEdit} size="small" color="primary">
                <EditIcon />
              </IconButton>
            ) : isEditing ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  onClick={handleSave}
                  size="small"
                  color="primary"
                  disabled={updateConfigMutation.isPending}
                >
                  {updateConfigMutation.isPending ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SaveIcon />
                  )}
                </IconButton>
                <IconButton
                  onClick={handleCancel}
                  size="small"
                  color="secondary"
                  disabled={updateConfigMutation.isPending}
                >
                  <CancelIcon />
                </IconButton>
              </Box>
            ) : null}
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            }}
          >
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <BusinessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" fontWeight={500}>
                  SurePrep Office Location
                </Typography>
              </Box>
              {isEditing ? (
                <FormControl fullWidth size="small" sx={{ ml: 2.5 }}>
                  <InputLabel id="office-location-label">
                    Select Office Location
                  </InputLabel>
                  <Select
                    labelId="office-location-label"
                    value={
                      editedConfig.officeLocationId &&
                      officeLocationList?.some(
                        (loc) => loc.Id === editedConfig.officeLocationId,
                      )
                        ? editedConfig.officeLocationId
                        : ""
                    }
                    label="Select Office Location"
                    onChange={(e) =>
                      handleOfficeLocationChange(Number(e.target.value))
                    }
                    disabled={
                      updateConfigMutation.isPending ||
                      isLoadingOfficeLocations ||
                      !officeLocationList
                    }
                  >
                    <MenuItem value="">
                      <em>None Selected</em>
                    </MenuItem>
                    {officeLocationList?.map((location) => (
                      <MenuItem key={location.Id} value={location.Id}>
                        {location.Name} (ID: {location.Id})
                      </MenuItem>
                    ))}
                    {/* Show current value even if not in list (when API fails) */}
                    {editedConfig.officeLocationId &&
                      !officeLocationList?.some(
                        (loc) => loc.Id === editedConfig.officeLocationId,
                      ) &&
                      officeLocationsError && (
                        <MenuItem value={editedConfig.officeLocationId}>
                          <em>
                            Current: ID {editedConfig.officeLocationId} (API
                            Error)
                          </em>
                        </MenuItem>
                      )}
                  </Select>
                  {officeLocationsError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      Error loading office locations
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 2.5 }}
                >
                  {(() => {
                    const selectedLocation = officeLocationList?.find(
                      (loc) => loc.Id === firm.surePrepConfig.officeLocationId,
                    );
                    return selectedLocation
                      ? `${selectedLocation.Name} (ID: ${selectedLocation.Id})`
                      : `ID: ${firm.surePrepConfig.officeLocationId}`;
                  })()}
                </Typography>
              )}
            </Box>

            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <SettingsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" fontWeight={500}>
                  Tax Software
                </Typography>
              </Box>
              {isEditing ? (
                <FormControl fullWidth size="small" sx={{ ml: 2.5 }}>
                  <InputLabel id="tax-software-label">
                    Select Tax Software
                  </InputLabel>
                  <Select
                    labelId="tax-software-label"
                    value={
                      editedConfig.taxSoftwareId &&
                      taxSoftwareList?.some(
                        (sw) => sw.Id === editedConfig.taxSoftwareId,
                      )
                        ? editedConfig.taxSoftwareId
                        : ""
                    }
                    label="Select Tax Software"
                    onChange={(e) =>
                      handleTaxSoftwareChange(Number(e.target.value))
                    }
                    disabled={
                      updateConfigMutation.isPending ||
                      isLoadingTaxSoftware ||
                      !taxSoftwareList
                    }
                  >
                    <MenuItem value="">
                      <em>None Selected</em>
                    </MenuItem>
                    {taxSoftwareList?.map((software) => (
                      <MenuItem key={software.Id} value={software.Id}>
                        {software.Name}
                      </MenuItem>
                    ))}
                    {/* Show current value even if not in list (when API fails) */}
                    {editedConfig.taxSoftwareId &&
                      !taxSoftwareList?.some(
                        (sw) => sw.Id === editedConfig.taxSoftwareId,
                      ) &&
                      taxSoftwareError && (
                        <MenuItem value={editedConfig.taxSoftwareId}>
                          <em>
                            Current: ID {editedConfig.taxSoftwareId} (API Error)
                          </em>
                        </MenuItem>
                      )}
                  </Select>
                  {taxSoftwareError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      Error loading tax software list
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 2.5 }}
                >
                  {firm.surePrepConfig.taxSoftwareName || "Not selected"}
                </Typography>
              )}
            </Box>

            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <AssignmentIcon
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
                <Typography variant="body2" fontWeight={500}>
                  Tax Software Account Number
                </Typography>
              </Box>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  value={editedConfig.accountNumber}
                  onChange={(e) =>
                    handleConfigFieldChange("accountNumber", e.target.value)
                  }
                  disabled={updateConfigMutation.isPending}
                  sx={{ ml: 2.5 }}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 2.5 }}
                >
                  {firm.surePrepConfig.accountNumber}
                </Typography>
              )}
            </Box>
          </Box>

          {isEditing && (
            <Box sx={{ mt: 3, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
              <Typography variant="caption" color="info.dark">
                <strong>Note:</strong> Changes to SurePrep configuration may
                affect job processing. Ensure these values match your SurePrep
                account settings.
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
